import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, timeout: 120000 });

/** Nudge the serverless DB awake (fire-and-forget) so a following login is fast. */
export function prewarm() {
  axios.get(`${API}/warmup`, { timeout: 10000 }).catch(() => {});
}

/** Block until the serverless DB reports ready (or the budget runs out).
 *  /api/warmup never touches the DB, so this is cheap; call it before login. */
export async function ensureDbReady(maxMs = 70000) {
  const start = Date.now();
  while (Date.now() - start < maxMs) {
    try {
      const { data } = await axios.get(`${API}/warmup`, { timeout: 8000 });
      if (data?.db_ready) return true;
    } catch { /* app still booting — keep waiting */ }
    await new Promise((r) => setTimeout(r, 2500));
  }
  return false; // let the request itself retry via the interceptor
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ep_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// The serverless DB auto-pauses; the first request after that gets a 503
// "database is waking up" for ~20-60s. Retry transparently so no request the
// user makes ever surfaces that as an error.
const isWakingUp = (res) =>
  res?.status === 503 &&
  (!!res.headers?.["retry-after"] ||
    /waking up|not currently available|resuming/i.test(
      typeof res.data === "string" ? res.data : JSON.stringify(res.data || "")
    ));

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const { config, response, code } = err;
    if (response?.status === 401) localStorage.removeItem("ep_token");
    if (!config) return Promise.reject(err);

    // 503 "waking up": retry for ~70s. Bare network error (deploy/restart): retry a few times.
    const waking = isWakingUp(response);
    const netDown = !response && (code === "ERR_NETWORK" || code === "ECONNABORTED");
    const cap = waking ? 14 : netDown ? 5 : 0;
    if (cap && (config.__dbRetry || 0) < cap) {
      config.__dbRetry = (config.__dbRetry || 0) + 1;
      prewarm(); // keep nudging the warmer
      await sleep(Math.min(2000 + config.__dbRetry * 1500, 6000));
      return api(config);
    }
    return Promise.reject(err);
  }
);

let _statusCache = null;
/** Lightweight feature-flag lookup ({ whatsapp: "live" | "mock" }); no DB, cached for the session. */
export async function getStatus() {
  if (_statusCache) return _statusCache;
  try {
    _statusCache = (await axios.get(`${API}/public/status`, { timeout: 15000 })).data;
  } catch {
    _statusCache = { whatsapp: "mock" };
  }
  return _statusCache;
}

export function apiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
